import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Line, 
  Pie, 
  Bar, 
  Doughnut 
} from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip, 
  Legend, 
  ArcElement,
  Filler
} from 'chart.js';
import { format } from 'date-fns';
import { dashboardService, recordService } from '../../services/api';
import { DashboardSummary, EnhancedAnalytics, TimeRange, Currency } from '../../types';
import DateRangePicker from '../common/DateRangePicker';
import '../../styles/Dashboard.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip, 
  Legend, 
  ArcElement,
  Filler
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [analytics, setAnalytics] = useState<EnhancedAnalytics | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('thisMonth');
  const [dateRange, setDateRange] = useState<{ startDate: Date | null, endDate: Date | null }>({
    startDate: null,
    endDate: null
  });
  const [currency, setCurrency] = useState<Currency>('vnd');
  const [accountType, setAccountType] = useState<'personal' | 'company'>('company');
  const [currentMonthOffset, setCurrentMonthOffset] = useState<number>(0); // 0 = this month, -1 = last month, +1 = next month
  
  // State for current balance (all transactions)
  const [currentBalance, setCurrentBalance] = useState({ total_vnd: 0, total_usd: 0 });
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchDashboardData();
    fetchCurrentBalance();
  }, [timeRange, dateRange, currency, accountType, currentMonthOffset]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      let params: any = { timeRange, currency, accountType };
      
      if (timeRange === 'custom' && dateRange.startDate && dateRange.endDate) {
        params.startDate = format(dateRange.startDate, 'yyyy-MM-dd');
        params.endDate = format(dateRange.endDate, 'yyyy-MM-dd');
      } else if (timeRange === 'thisMonth' && currentMonthOffset !== 0) {
        // Calculate the target month based on offset
        const targetDate = new Date();
        targetDate.setMonth(targetDate.getMonth() + currentMonthOffset);
        const startOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
        const endOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
        
        params.timeRange = 'custom';
        params.startDate = format(startOfMonth, 'yyyy-MM-dd');
        params.endDate = format(endOfMonth, 'yyyy-MM-dd');
      }
      
      const [summaryResponse, analyticsResponse] = await Promise.all([
        dashboardService.getSummary(params),
        dashboardService.getAnalytics(params)
      ]);
      
      setSummary(summaryResponse.data);
      setAnalytics(analyticsResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch current balance (all transactions) - same as Records component
  const fetchCurrentBalance = async () => {
    setBalanceLoading(true);
    
    try {
      const params: any = {
        // Only filter by the currently selected account type
        accountType: accountType
      };
      
      console.log('Fetching current balance with params:', params);
      const response = await recordService.getCurrentBalance(params);
      console.log('Current balance response:', response.data);
      
      const balanceData = response.data;
      setCurrentBalance({
        total_vnd: balanceData.total_vnd || 0,
        total_usd: balanceData.total_usd || 0
      });
      
    } catch (error) {
      console.error('Error fetching current balance:', error);
      // Don't show error notification for balance - it's not critical for page functionality
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleTimeRangeChange = (newRange: TimeRange) => {
    setTimeRange(newRange);
    if (newRange !== 'thisMonth') {
      setCurrentMonthOffset(0); // Reset month offset when changing away from "This Month"
    }
  };

  const handleDateRangeChange = (startDate: Date | null, endDate: Date | null) => {
    setDateRange({ startDate, endDate });
  };

  const handleCurrencyToggle = () => {
    setCurrency(prev => prev === 'vnd' ? 'usd' : 'vnd');
  };

  const handleAccountTypeChange = (type: 'personal' | 'company') => {
    setAccountType(type);
  };

  const handlePreviousMonth = () => {
    setTimeRange('thisMonth');
    setCurrentMonthOffset(prev => prev - 1);
  };

  const handleNextMonth = () => {
    setTimeRange('thisMonth');
    setCurrentMonthOffset(prev => prev + 1);
  };

  const getCurrentMonthLabel = () => {
    if (timeRange !== 'thisMonth') return 'This Month';
    
    const targetDate = new Date();
    targetDate.setMonth(targetDate.getMonth() + currentMonthOffset);
    
    if (currentMonthOffset === 0) return 'This Month';
    if (currentMonthOffset === -1) return 'Last Month';
    if (currentMonthOffset === 1) return 'Next Month';
    
    return format(targetDate, 'MMM yyyy');
  };

  const formatCurrency = (amount: number) => {
    if (currency === 'vnd') {
      // Format VND with commas and no decimals, consistent with Records page
      const formatted = Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return `${amount < 0 ? '-' : ''}${formatted} ₫`;
    } else {
      // Format USD with commas and no decimals, consistent with Records page
      const formatted = Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return `${amount < 0 ? '-' : ''}$${formatted}`;
    }
  };

  // Calculate frame balance (filtered period balance)
  const calculateFrameBalance = () => {
    if (!summary) return 0;
    return summary.summary.totalIncome + summary.summary.totalExpense; // expense is already negative
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getTrendIcon = (value: number) => {
    if (value > 0) return '📈';
    if (value < 0) return '📉';
    return '➡️';
  };

  const getTrendColor = (value: number) => {
    if (value > 0) return '#2ecc71';
    if (value < 0) return '#e74c3c';
    return '#95a5a6';
  };

  // Navigation handlers for chart clicks
  const handleCategoryClick = useCallback((categoryId: number, recordType?: string) => {
    const filters = {
      categoryId,
      recordType,
      accountType,
      timeRange,
      ...(timeRange === 'custom' && dateRange.startDate && dateRange.endDate ? {
        startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
        endDate: format(dateRange.endDate, 'yyyy-MM-dd')
      } : {})
    };
    
    // Navigate to Records with pre-applied filters
    navigate('/records', { state: { filters } });
  }, [navigate, accountType, timeRange, dateRange]);

  const handleDateClick = useCallback((date: string) => {
    const filters = {
      startDate: date,
      endDate: date,
      accountType
    };
    
    navigate('/records', { state: { filters } });
  }, [navigate, accountType]);

  // Chart configuration options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 20
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          boxWidth: 12,
          boxHeight: 12
        }
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#64748b',
          padding: 10
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f1f5f9'
        },
        ticks: {
          color: '#64748b',
          padding: 10,
          callback: function(value: any) {
            return formatCurrency(value);
          }
        }
      }
    },
    onClick: (event: any, elements: any) => {
      if (elements.length > 0) {
        const elementIndex = elements[0].index;
        const datasetIndex = elements[0].datasetIndex;
        const recordType = datasetIndex === 0 ? 'income' : 'expense';
        const chartData = summary?.chartData?.[elementIndex];
        if (chartData) {
          navigate('/records', {
            state: {
              filters: {
                recordType,
                dateRange: {
                  startDate: new Date(chartData.date),
                  endDate: new Date(chartData.date)
                },
                accountType
              }
            }
          });
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10
      }
    },
    plugins: {
      legend: {
        position: window.innerWidth < 768 ? 'bottom' as const : 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          },
          boxWidth: 12,
          boxHeight: 12,
          generateLabels: function(chart: any) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const dataset = data.datasets[0];
              return data.labels.map((label: string, i: number) => {
                const value = dataset.data[i];
                const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.backgroundColor[i],
                  lineWidth: 0,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: ${formatCurrency(value)} (${percentage}%)`;
          }
        }
      }
    },
    onClick: (event: any, elements: any) => {
      if (elements.length > 0) {
        const elementIndex = elements[0].index;
        const categoryData = analytics?.expenseDistribution?.[elementIndex] || analytics?.incomeSources?.[elementIndex];
        if (categoryData) {
          handleCategoryClick(categoryData.categoryId, elements[0].datasetIndex === 0 ? 'expense' : 'income');
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 20
      }
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          boxWidth: 12,
          boxHeight: 12
        }
      },
      title: {
        display: true,
        text: 'Monthly Trend (Last 12 Months)',
        color: '#1e293b',
        font: {
          size: 16,
          weight: 'bold' as const
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#64748b',
          padding: 10
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f1f5f9'
        },
        ticks: {
          color: '#64748b',
          padding: 10,
          callback: function(value: any) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  // Prepare chart data with validation
  const lineChartData = {
    labels: summary?.chartData?.map(item => format(new Date(item.date), 'MMM dd')) || [],
    datasets: [
      {
        label: 'Income',
        data: summary?.chartData?.map(item => Number(item.income) || 0) || [],
        borderColor: '#2ecc71',
        backgroundColor: 'rgba(46, 204, 113, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#2ecc71',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      },
      {
        label: 'Expense',
        data: summary?.chartData?.map(item => Math.abs(Number(item.expense) || 0)) || [],
        borderColor: '#e74c3c',
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#e74c3c',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]
  };

  const expensePieData = {
    labels: analytics?.expenseDistribution?.map(cat => cat.categoryName) || [],
    datasets: [
      {
        data: analytics?.expenseDistribution?.map(cat => Number(cat.amount) || 0) || [],
        backgroundColor: [
          '#e74c3c', '#f39c12', '#3498db', '#9b59b6', '#2ecc71',
          '#e67e22', '#95a5a6', '#2980b9', '#c0392b', '#d35400'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const incomePieData = {
    labels: analytics?.incomeSources?.map(cat => cat.categoryName) || [],
    datasets: [
      {
        data: analytics?.incomeSources?.map(cat => Number(cat.amount) || 0) || [],
        backgroundColor: [
          '#2ecc71', '#3498db', '#f39c12', '#9b59b6', '#26c281',
          '#34495e', '#16a085', '#27ae60', '#2471a3', '#239b56'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const monthlyTrendData = {
    labels: analytics?.monthlyTrend?.map(item => {
      try {
        // Handle month format - could be 'YYYY-MM' or 'YYYY-MM-DD'
        const monthStr = typeof item.month === 'string' ? item.month : String(item.month || '');
        if (monthStr) {
          const date = monthStr.includes('-01') ? new Date(monthStr) : new Date(monthStr + '-01');
          return format(date, 'MMM yyyy');
        }
        return 'Unknown';
      } catch (error) {
        console.warn('Error formatting month:', item.month, error);
        return 'Unknown';
      }
    }) || [],
    datasets: [
      {
        label: 'Income',
        data: analytics?.monthlyTrend?.map(item => Number(item.income) || 0) || [],
        backgroundColor: 'rgba(46, 204, 113, 0.8)',
        borderColor: '#2ecc71',
        borderWidth: 1
      },
      {
        label: 'Expense',
        data: analytics?.monthlyTrend?.map(item => Math.abs(Number(item.expense) || 0)) || [],
        backgroundColor: 'rgba(231, 76, 60, 0.8)',
        borderColor: '#e74c3c',
        borderWidth: 1
      }
    ]
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (!summary || !analytics) {
    return (
      <div className="dashboard">
        <div className="no-data-message">
          <i className="fas fa-info-circle"></i>
          <p>No dashboard data available. Start by adding some records.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-controls">
          {/* Left Column: Account Type Toggle */}
          <div className="control-section account-section">
            <label className="control-label">Account</label>
            <div className="account-toggle">
              <input
                type="checkbox"
                id="accountToggle"
                checked={accountType === 'company'}
                onChange={() => handleAccountTypeChange(accountType === 'personal' ? 'company' : 'personal')}
                className="account-input"
              />
              <label htmlFor="accountToggle" className="account-switch">
                <span className="account-option personal">
                  <i className="fas fa-user"></i>
                  <span>Personal</span>
                </span>
                <span className="account-option company">
                  <i className="fas fa-building"></i>
                  <span>Company</span>
                </span>
                <div className="account-slider"></div>
              </label>
            </div>
          </div>
          
          {/* Middle Column: Date Range Filter */}
          <div className="control-section date-section">
            <label className="control-label">Period</label>
            <div className="date-filter-container">
              <div className="time-range-selector">
                <button 
                  className={`time-range-btn ${timeRange === 'today' ? 'active' : ''}`} 
                  onClick={() => handleTimeRangeChange('today')}
                >
                  Today
                </button>
                <button 
                  className={`time-range-btn ${timeRange === 'thisWeek' ? 'active' : ''}`} 
                  onClick={() => handleTimeRangeChange('thisWeek')}
                >
                  Week
                </button>
                <div className="month-navigation">
                  <button 
                    className="month-nav-btn"
                    onClick={handlePreviousMonth}
                    title="Previous Month"
                  >
                    ◀
                  </button>
                  <div className="month-display">
                    {getCurrentMonthLabel()}
                  </div>
                  <button 
                    className="month-nav-btn"
                    onClick={handleNextMonth}
                    title="Next Month"
                  >
                    ▶
                  </button>
                </div>
                <button 
                  className={`time-range-btn ${timeRange === 'thisYear' ? 'active' : ''}`} 
                  onClick={() => handleTimeRangeChange('thisYear')}
                >
                  Year
                </button>
                <button 
                  className={`time-range-btn ${timeRange === 'custom' ? 'active' : ''}`} 
                  onClick={() => handleTimeRangeChange('custom')}
                >
                  Custom
                </button>
              </div>
              
              {timeRange === 'custom' && (
                <DateRangePicker 
                  startDate={dateRange.startDate} 
                  endDate={dateRange.endDate} 
                  onChange={handleDateRangeChange} 
                />
              )}
            </div>
          </div>
          
          {/* Right Column: Currency Toggle */}
          <div className="control-section currency-section">
            <label className="control-label">Currency</label>
            <div className="currency-toggle">
              <input
                type="checkbox"
                id="currencyToggle"
                checked={currency === 'usd'}
                onChange={handleCurrencyToggle}
                className="currency-input"
              />
              <label htmlFor="currencyToggle" className="currency-switch">
                <span className="currency-option vnd">VND</span>
                <span className="currency-option usd">USD</span>
                <div className="currency-slider"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Enhanced Summary Cards with Trends */}
      {!loading && summary && analytics && (
      <div className="dashboard-summary">
        <div className="summary-card balance">
          <div className="summary-header">
            <h3>Current Balance</h3>
            <span className="trend-indicator" style={{ color: getTrendColor(analytics.summary.changes.balanceChangePercent) }}>
              {getTrendIcon(analytics.summary.changes.balanceChangePercent)}
            </span>
          </div>
          <div className="summary-value large-balance">{formatCurrency(currency === 'vnd' ? currentBalance.total_vnd : currentBalance.total_usd)}</div>
          <div className="summary-subtitle">All transactions (cached)</div>
        </div>

        <div className="summary-card income">
          <div className="summary-header">
            <h3>Total Income</h3>
            <span className="trend-indicator" style={{ color: getTrendColor(analytics.summary.changes.incomeChangePercent) }}>
              {getTrendIcon(analytics.summary.changes.incomeChangePercent)}
            </span>
          </div>
          <div className="summary-value">{formatCurrency(summary.summary.totalIncome)}</div>
          <div className="summary-change" style={{ color: getTrendColor(analytics.summary.changes.incomeChangePercent) }}>
            {formatCurrency(analytics.summary.changes.incomeChange)} ({formatPercentage(analytics.summary.changes.incomeChangePercent)})
          </div>
          <div className="summary-subtitle">{analytics.summary.current.incomeCount} transactions</div>
        </div>

        <div className="summary-card expense">
          <div className="summary-header">
            <h3>Total Expense</h3>
            <span className="trend-indicator" style={{ color: getTrendColor(-analytics.summary.changes.expenseChangePercent) }}>
              {getTrendIcon(-analytics.summary.changes.expenseChangePercent)}
            </span>
          </div>
          <div className="summary-value">{formatCurrency(Math.abs(summary.summary.totalExpense))}</div>
          <div className="summary-change" style={{ color: getTrendColor(-analytics.summary.changes.expenseChangePercent) }}>
            {formatCurrency(Math.abs(analytics.summary.changes.expenseChange))} ({formatPercentage(analytics.summary.changes.expenseChangePercent)})
          </div>
          <div className="summary-subtitle">{analytics.summary.current.expenseCount} transactions</div>
        </div>

        <div className="summary-card tax">
          <div className="summary-header">
            <h3>Total Tax</h3>
            <i className="fas fa-receipt"></i>
          </div>
          <div className="summary-value">{formatCurrency(summary.summary.totalTax)}</div>
          <div className="summary-subtitle">Current period</div>
        </div>

        <div className="summary-card frame-balance">
          <div className="summary-header">
            <h3>Time-framed Balance</h3>
            <span className="trend-indicator" style={{ color: getTrendColor(calculateFrameBalance()) }}>
              {getTrendIcon(calculateFrameBalance())}
            </span>
          </div>
          <div className="summary-value">{formatCurrency(calculateFrameBalance())}</div>
          <div className="summary-subtitle">Income - Expense (filtered period)</div>
        </div>
      </div>
      )}

      {loading && (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      )}

      {/* Charts Section */}
      {!loading && summary && analytics && (
      <div className="dashboard-charts">
        {/* Main Trend Chart */}
        <div className="chart-container main-chart">
          <div className="chart-header">
            <h3>Income & Expense Trend</h3>
            <p className="chart-subtitle">Click on data points to view detailed records</p>
          </div>
          <div className="chart-content">
            {summary?.chartData && summary.chartData.length > 0 ? (
              <Line data={lineChartData} options={lineChartOptions} />
            ) : (
              <div className="no-data">No trend data available for the selected period</div>
            )}
          </div>
        </div>

        {/* Pie Charts Row */}
        <div className="charts-row">
          <div className="chart-container">
            <div className="chart-header">
              <h3>Expense Distribution</h3>
              <p className="chart-subtitle">Click to filter by category</p>
            </div>
            <div className="chart-content">
              {analytics.expenseDistribution.length > 0 ? (
                <Doughnut data={expensePieData} options={pieChartOptions} />
              ) : (
                <div className="no-data">No expense data available</div>
              )}
            </div>
          </div>
          
          <div className="chart-container">
            <div className="chart-header">
              <h3>Income Sources</h3>
              <p className="chart-subtitle">Click to filter by category</p>
            </div>
            <div className="chart-content">
              {analytics.incomeSources.length > 0 ? (
                <Doughnut data={incomePieData} options={pieChartOptions} />
              ) : (
                <div className="no-data">No income data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Monthly Trend Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Monthly Trend Analysis</h3>
            <p className="chart-subtitle">12-month overview</p>
          </div>
          <div className="chart-content">
            {analytics.monthlyTrend && analytics.monthlyTrend.length > 0 ? (
              <Bar data={monthlyTrendData} options={barChartOptions} />
            ) : (
              <div className="no-data">No monthly trend data available</div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Top Categories Section */}
      {!loading && summary && analytics && (
      <div className="top-categories">
        <h3>Top Categories by Amount</h3>
        <div className="categories-grid">
          {analytics.topCategories.slice(0, 6).map((category) => (
            <div 
              key={`${category.categoryId}-${category.recordType}`}
              className={`category-card ${category.recordType}`}
              onClick={() => handleCategoryClick(category.categoryId, category.recordType)}
            >
              <div className="category-header">
                <span className="category-name">{category.categoryName}</span>
                <span className={`category-type ${category.recordType}`}>
                  {category.recordType === 'income' ? '📈' : '📉'}
                </span>
              </div>
              <div className="category-amount">{formatCurrency(category.amount)}</div>
              <div className="category-details">
                {category.recordCount} transactions
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Quick Actions */}
      {!loading && (
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button 
            className="action-btn primary"
            onClick={() => navigate('/records')}
          >
            <i className="fas fa-plus"></i>
            Add New Record
          </button>
          <button 
            className="action-btn secondary"
            onClick={() => navigate('/records', { state: { showFilters: true } })}
          >
            <i className="fas fa-filter"></i>
            View All Records
          </button>
          <button 
            className="action-btn secondary"
            onClick={() => navigate('/categories')}
          >
            <i className="fas fa-tags"></i>
            Manage Categories
          </button>
          <button 
            className="action-btn secondary"
            onClick={() => window.location.reload()}
          >
            <i className="fas fa-sync-alt"></i>
            Refresh Data
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default Dashboard;
